"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { GeistMono } from "geist/font/mono";
import { GeistSans } from "geist/font/sans";
import { CircleSlashIcon } from "lucide-react";

import { captureEvent } from "@acme/monitoring/client";

import { Card, CardContent, CardHeader } from "@/components/ui/card";

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    Sentry.captureException(error);
    captureEvent({
      code: "WEBSITE_FRONTEND_CRASH",
      level: "error",
      context: {
        error: error.message,
        errorName: error.name,
      },
    });
  }, [error]);

  return (
    <html>
      <body
        className={`${GeistSans.variable} ${GeistMono.variable} font-sans antialiased`}
      >
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mb-4 flex justify-center">
                <CircleSlashIcon className="h-12 w-12 text-muted-foreground" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">
                We encountered an error
              </h1>
              <p className="text-sm text-muted-foreground">
                Our team has been notified and we&apos;re working to resolve
                this issue.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-muted p-4">
                <p className="font-mono text-sm text-muted-foreground">
                  Error: {error.message || "An unexpected error occurred"}
                </p>
              </div>
              <p className="text-center text-sm text-muted-foreground">
                You can{" "}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.reload();
                  }}
                  className="text-primary hover:underline"
                >
                  refresh the page
                </button>{" "}
                to try again, or{" "}
                {/* oxlint-disable-next-line nextjs/no-html-link-for-pages */}
                <a href="/overview" className="text-primary hover:underline">
                  return to the dashboard
                </a>
                .
              </p>
            </CardContent>
          </Card>
        </div>
      </body>
    </html>
  );
}
